import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronRight, LayoutTemplate, MoreHorizontal, Play, Plus } from 'lucide-react';
import { type JSX, useRef, useState } from 'react';
import { Button, ConfirmDialog } from '~/components/primitives';
import { CreateBlueprintDialog } from '~/components/tasks/blueprints/CreateBlueprintDialog';
import { MaterializeDialog } from '~/components/tasks/blueprints/MaterializeDialog';
import { formatDate } from '~/lib/format';
import {
  type BlueprintListItem,
  deactivateBlueprint,
  deleteBlueprint,
  fetchBlueprints,
} from '~/lib/tasks/api/blueprints';
import { PROJECT_KIND_LABEL, labelOf } from '~/lib/tasks/i18n/labels';
import shared from './_auth._shared.module.css';
import styles from './_auth.work-blueprints.module.css';

/**
 * `/work-blueprints` → «Шаблоны проектов» (project-templates F2.1/F2.7).
 * Карточки снапшот-шаблонов: task_count, версия, источник, дата. CTA «Создать
 * проект» → диалог материализации. Overflow «…»: деактивация / удаление.
 * Путь создания шаблона — «Сохранить как шаблон» из проекта (не отсюда).
 */

const KEY = ['task-module', 'blueprints'];

export const Route = createFileRoute('/_auth/work-blueprints')({
  component: BlueprintsPage,
});

function BlueprintsPage(): JSX.Element {
  const qc = useQueryClient();
  const [materializing, setMaterializing] = useState<BlueprintListItem | null>(null);
  const [confirm, setConfirm] = useState<{ bp: BlueprintListItem; hard: boolean } | null>(null);
  const [creating, setCreating] = useState(false);

  const query = useQuery({ queryKey: KEY, queryFn: fetchBlueprints });

  const lifecycle = useMutation({
    mutationFn: ({ id, hard }: { id: string; hard: boolean }) =>
      hard ? deleteBlueprint(id) : deactivateBlueprint(id),
    onSuccess: () => {
      setConfirm(null);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });

  const blueprints = query.data ?? [];

  return (
    <div className={shared.page}>
      <div className={styles.headerBar}>
        <h1 className={styles.title}>
          <LayoutTemplate size={20} />
          {m.tm_route_project_templates()}
        </h1>
        {/* F14 — путь «создать шаблон, не имея проекта-источника». Единственный
            залитый primary на странице (F-B2): создание — то самое действие,
            ради которого сюда приходят с пустым списком. */}
        <Button leadingIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
          {m.tmpl_create_submit()}
        </Button>
      </div>

      {blueprints.length === 0 ? (
        <p className={styles.empty}>{m.tmpl_page_empty()}</p>
      ) : (
        <ul className={styles.list}>
          {blueprints.map((bp) => (
            <li key={bp.id} className={styles.item}>
              <div className={styles.itemInfo}>
                {/* F1 — имя карточки ведёт на страницу просмотра: до этого пака
                    единственным действием с шаблоном было «развернуть проект». */}
                {/* F-B3 — affordance просмотра. Раньше заголовок был ссылкой
                    только технически: цвет текста, без подчёркивания и без
                    признака перехода, а единственным явным действием на карточке
                    была залитая кнопка «Создать проект». Владелец, жаловавшийся
                    «нельзя посмотреть», новую страницу мог не найти. */}
                <Link
                  to="/work-blueprints/$id"
                  params={{ id: bp.id }}
                  className={styles.itemNameLink}
                >
                  {bp.name}
                  <ChevronRight size={14} aria-hidden="true" />
                </Link>
                {/* D8.2 (DR-F5), вторая половина находки — РЕКОНСИЛЯЦИЯ, признак
                    состояния здесь не нужен. Проверено живым прогоном: список
                    ручки `GET /blueprints` отдаёт ТОЛЬКО активные, и
                    деактивированный шаблон уходит из выдачи целиком. То есть
                    результат «Деактивировать» виден — строка исчезает, — а бейдж
                    «Неактивен» был бы мёртвой разметкой: условие не выполнимо ни
                    при каких данных. Настоящий пробел другой: увидеть
                    деактивированные шаблоны негде вовсе (нет ни фильтра, ни
                    вида) — заведено строкой нового цикла. */}
                <span className={styles.itemMeta}>
                  {labelOf(PROJECT_KIND_LABEL, bp.kind as never)} ·{' '}
                  {m.tm_route_tasks_count({ count: bp.task_count })} ·{' '}
                  {/* D4.6 (W-2) — идентификаторы моно, как ProvenanceChip и коды
                      проектов везде: «v2» и «PROJ-2026-9001» набором не отличались
                      от слов, и глаз не находил их в потоке меты. */}
                  <span className={styles.itemMetaCode}>v{bp.version}</span>
                  {/* F6 — «план: N дней» / «нет дат»: подтверждение, что внутри
                      только смещения, а не привязка к прошлым периодам. */}
                  {` · ${
                    bp.has_dates && bp.max_offset_days !== null
                      ? m.tmpl_view_plan_days({ days: bp.max_offset_days })
                      : m.tmpl_view_no_dates()
                  }`}
                  {bp.previous_versions_count > 0 &&
                    ` · ${m.tmpl_card_versions_prev({ count: bp.previous_versions_count })}`}
                  {bp.source && ` · ${m.tmpl_card_source({ name: bp.source.project_name })}`}
                  {bp.source?.project_code && (
                    <>
                      {' · '}
                      <span className={styles.itemMetaCode}>{bp.source.project_code}</span>
                    </>
                  )}
                  {` · ${m.tmpl_card_updated({ date: formatDate(bp.updated_at) })}`}
                </span>
              </div>
              <div className={styles.itemActions}>
                <Link to="/work-blueprints/$id" params={{ id: bp.id }} className={styles.openBtn}>
                  {m.tmpl_view_open_action()}
                </Link>
                {/* F-B2 — ряд одинаковых залитых accent-кнопок превращал список
                    в столбец primary-действий: каждая строка кричала одинаково
                    громко, и accent конкурировал сам с собой. Здесь действие
                    строчное (secondary), залитый primary остался один — в шапке
                    страницы шаблона. */}
                <button
                  type="button"
                  className={styles.applyBtn}
                  onClick={() => setMaterializing(bp)}
                >
                  <Play size={14} />
                  {m.tmpl_cta_create_project()}
                </button>
                <CardMenu
                  onDeactivate={() => setConfirm({ bp, hard: false })}
                  onDelete={() => setConfirm({ bp, hard: true })}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      {creating && <CreateBlueprintDialog onClose={() => setCreating(false)} />}

      {materializing && (
        <MaterializeDialog
          blueprint={{ id: materializing.id, name: materializing.name }}
          onClose={() => setMaterializing(null)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          open
          variant={confirm.hard ? 'danger' : 'default'}
          // F100 круг 3 (UX): деактивацию из интерфейса пока не вернуть — для пользователя она
          // необратима: «Отмена» по умолчанию и в фокусе, как у удаления.
          emphasizeCancel
          // Evidence-first: подтверждение называет, что именно — шаблон и его версию.
          meta={`${confirm.bp.name} · v${confirm.bp.version}`}
          title={confirm.hard ? m.tmpl_delete_confirm_title() : m.tmpl_deactivate_confirm_title()}
          description={
            confirm.hard ? m.tmpl_delete_confirm_body() : m.tmpl_deactivate_confirm_body()
          }
          confirmLabel={confirm.hard ? m.tmpl_card_menu_delete() : m.tmpl_card_menu_deactivate()}
          cancelLabel={m.tm_cancel()}
          onCancel={() => setConfirm(null)}
          onConfirm={() => lifecycle.mutate({ id: confirm.bp.id, hard: confirm.hard })}
        />
      )}
    </div>
  );
}

/** Overflow-меню карточки шаблона (деактивация / удаление). */
function CardMenu({
  onDeactivate,
  onDelete,
}: {
  onDeactivate: () => void;
  onDelete: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      className={styles.menuWrap}
      ref={ref}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        className={styles.menuBtn}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={m.tm_route_project_templates()}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              onDeactivate();
            }}
          >
            {m.tmpl_card_menu_deactivate()}
          </button>
          {/* D8.2 (DR-F5) — разделитель перед деструктивным пунктом.
              Цвет был единственной защитой от промаха, а «Удалить» стояло
              вплотную к «Деактивировать»: два соседних пункта, у которых
              последствия отличаются обратимостью. Разделитель — тот же канон
              последней группы, что в примитиве ПКМ (инвариант I4). */}
          {/* biome-ignore lint/a11y/useFocusableInteractive: separator внутри role="menu" структурный — по WAI-ARIA он не участвует в обходе пунктов и фокус не принимает (тот же приём, что в примитиве ContextMenuSeparator) */}
          <div className={styles.menuSep} role="separator" />
          <button
            type="button"
            role="menuitem"
            className={styles.menuItemDanger}
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            <span>{m.tmpl_card_menu_delete()}</span>
            {/* Необратимость названа словом, а не оттенком: деактивацию можно
                отменить, удаление — нет, и это единственное, что отличает
                соседние пункты по последствию. */}
            <span className={styles.menuItemNote}>{m.tmpl_card_menu_delete_note()}</span>
          </button>
        </div>
      )}
    </div>
  );
}
